/**
 * Points of Sale (POS) queries for TanStack Query (Vendor App)
 */

import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet } from '../utils/request'

// Type definitions
export interface PointOfSale {
  id: number
  company_id: number
  description: string
  code: string
  status: string
  created_at?: string
  updated_at?: string
}

export interface POSListParams {
  search?: string
  company_id?: number
  status?: string
  page?: number
  per_page?: number
  [key: string]: any
}

export interface POSListResponse {
  items: PointOfSale[]
  total?: number
  page?: number
  per_page?: number
}

// Cache configuration
const CACHE_TIMES = {
  POS: 5 * 60 * 1000, // 5 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of points of sale
 */
export const usePOSQuery = (params: MaybeRefOrGetter<POSListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.posList(toValue(params))),
    queryFn: async (): Promise<POSListResponse> => {
      const queryParams = toValue(params)
      return queryGet<POSListResponse>('/v1/points_of_sale/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.POS,
  })
}

/**
 * Query hook to fetch a single point of sale by ID
 */
export const usePOSItemQuery = (posId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(posId)
      return id ? queryKeys.posItem(id) : ['pos', 'null']
    }),
    queryFn: async (): Promise<PointOfSale> => {
      const id = toValue(posId)
      if (!id) throw new Error('POS ID is required')
      return queryGet<PointOfSale>(`/v1/points_of_sale/${id}`)
    },
    staleTime: CACHE_TIMES.POS,
    enabled: computed(() => !!toValue(posId)),
  })
}
